<script setup lang="ts">
import { getRuneBasicAttrValue, getRuneName, runeCollection } from '@/core/rune'
import { useUserStore } from '@/stores/modules/user'
import type { RuneItemType, RuneSellRes } from '@/types/rune'
import { equipQualityText, RuneRoleText } from '../data'
import { BasicAttrTextEnum, EquipPriceEnum } from '@/enums'
import { explainRuneAffix } from '@/core/rune/affix'
import { soulClass } from '@/views/awaken/data'
import { computed } from 'vue'
import { formatNumberWithCommas } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'
import type { UserAttrType } from '@/types/user'
import { Lock, Unlock } from '@element-plus/icons-vue'

const userStore = useUserStore()

const props = withDefaults(
  defineProps<{ runeInfo: RuneItemType; isInItems?: boolean; isInComparison?: boolean; showAction?: boolean; showLock?: boolean }>(),
  {
    isInItems: true,
    isInComparison: false,
    showAction: true,
    showLock: true,
  },
)
const emits = defineEmits(['clear', 'lock', 'compare', 'closeCompare', 'sell'])

const soulType = computed(() => runeCollection[props.runeInfo.runeId].soulType)

async function handleEquipRune(action: 'wear' | 'remove', index?: number) {
  if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('背包已满')
  const id = props.runeInfo.id
  const res = await socketEmitAsync<UserAttrType>({ type: 'runeAction', data: { id, action, index } })
  if (action === 'wear') {
    userStore.runeEquippedList[index!] = id
  } else {
    const index = userStore.runeEquippedList.findIndex((item) => item === id)
    userStore.runeEquippedList[index!] = ''
  }
  userStore.userAttr = res
  userStore.setRoutineEquippedList()
  emits('clear')
  if (props.isInComparison) emits('closeCompare')
  ElMessage.success(action === 'wear' ? '携带成功' : '卸下成功')
}
async function handleLock() {
  const id = props.runeInfo.id
  await socketEmitAsync<RuneItemType[]>({ type: 'lockRune', data: { id } })
  const rune = userStore.runeList.find((item) => item.id === id)
  if (!rune) return
  rune.isLock = !rune.isLock
  emits('lock')
}
function handleSell() {
  const { runeInfo } = props
  const { quality = 'normal', runeId, isLock } = runeInfo
  if (isLock) return ElMessage.warning('符石已锁定，无法出售')
  ElMessageBox.confirm(`<p class="affix">确定出售【<span class="${quality}">${getRuneName(runeId)}</span>】吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { runeList, gold } = await socketEmitAsync<RuneSellRes>({ type: 'sellRune', data: { idList: [runeInfo.id] } })
      userStore.runeList = runeList
      userStore.gold = gold
      emits('clear')
      ElMessage.success('出售成功')
    })
    .catch(() => {})
}

const runeEquippedList = computed(() =>
  userStore.runeEquippedList.map((item) => {
    const rune = userStore.runeList.find((rune) => rune.id === item)
    if (!rune) return undefined
    return rune
  }),
)
const score = computed(() => {
  const mag = userStore.soulType === soulType.value ? 1.2 : 1
  let res = 0
  const basic = props.runeInfo.attrs.basic
  if (basic) {
    Object.values(basic).forEach((item) => {
      res += item
    })
  }
  props.runeInfo.attrs.special.forEach((item) => {
    Object.values(item.data).forEach((item) => {
      res += item
    })
  })
  return Number((res * mag).toFixed(1))
})
</script>

<template>
  <div class="rune-info affix">
    <p :class="runeInfo.quality" style="position: relative; margin-bottom: 8px; font-weight: bold">
      {{ getRuneName(runeInfo.runeId) }}
      <span class="rune-lock" v-show="showAction && showLock">
        <el-button size="small" :type="runeInfo.isLock ? 'success' : 'info'" plain @click="handleLock">
          <el-icon size="20" v-show="!runeInfo.isLock"><Unlock /></el-icon>
          <el-icon size="20" v-show="runeInfo.isLock"><Lock /></el-icon>
        </el-button>
      </span>
    </p>
    <p>
      品质：<span :class="runeInfo.quality">{{ equipQualityText[runeInfo?.quality || 'normal'] }}</span>
    </p>
    <p>
      所属：<span :class="soulClass[soulType]">{{ RuneRoleText[soulType] }}</span>
    </p>
    <p>
      评分：<span :class="runeInfo.quality">{{ score }}</span
      ><span class="awaken">{{ userStore.soulType === soulType ? `（+${(score - score / 1.2).toFixed(1)}）` : '' }}</span>
    </p>
    <p>
      售价：<span class="gold">{{ formatNumberWithCommas(EquipPriceEnum[runeInfo.quality] * 200) }}</span>
    </p>
    <template v-if="Object.keys(runeInfo.attrs.basic || {}).length">
      <p>基本属性：</p>
      <p v-for="(val, key) of runeInfo.attrs.basic">
        <span v-if="getRuneBasicAttrValue(key, val!, userStore.soulType === soulType) > 0">
          <span>{{ BasicAttrTextEnum[key] }} + </span>
          <span class="grow">
            {{ getRuneBasicAttrValue(key, val!, userStore.soulType === soulType) }}{{ ['crt', 'crtd', 'hr', 'atksp'].includes(key) ? '%' : '' }}
          </span>
        </span>
      </p>
    </template>
    <br />
    <template v-if="runeInfo.attrs.special.length">
      <p>特殊属性：</p>
      <p v-for="item of explainRuneAffix(runeInfo.attrs.special, userStore.soulType)" v-html="item"></p>
    </template>
  </div>
  <el-row class="equip-action" v-show="isInItems && showAction">
    <el-col :span="8">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn type="success" size="small">装备</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button
            v-for="(item, index) of runeEquippedList"
            :type="item ? 'primary' : 'info'"
            size="small"
            plain
            @click="handleEquipRune('wear', index)"
          >
            <span v-if="item" :class="item.quality">{{ getRuneName(item.runeId) }}</span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn type="primary" size="small">比对</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button v-for="(item, index) of runeEquippedList" :type="item ? 'primary' : 'info'" size="small" plain @click="emits('compare', index)">
            <span v-if="item" :class="item.quality">{{ getRuneName(item.runeId) }}</span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <CommonBtn type="warning" size="small" @done="handleSell">出售</CommonBtn>
    </el-col>
  </el-row>
  <el-row class="equip-action" v-show="!isInItems && showAction">
    <el-col :span="8">
      <CommonBtn type="danger" size="small" @done="handleEquipRune('remove')">卸下</CommonBtn>
    </el-col>
  </el-row>
</template>

<style lang="scss" scoped>
.rune-info {
  font-size: 14px;
  height: 100%;
  overflow: auto;
  margin-bottom: 10px;
  p {
    margin: 4px 0;
  }
  .rune-lock {
    position: absolute;
    right: 4px;
    top: calc(100% + 10px);
  }
}
:deep(.hour-list) {
  .el-button:focus,
  .el-button:hover {
    background-color: unset;
  }
}
</style>
